import { useI18n } from "@/i18n/I18nProvider";

const STEP_KEYS = [
    { n: "01", title: "how.s1.title", body: "how.s1.body" },
    { n: "02", title: "how.s2.title", body: "how.s2.body" },
    { n: "03", title: "how.s3.title", body: "how.s3.body" },
    { n: "04", title: "how.s4.title", body: "how.s4.body" },
];

const ADAPTIVE_KEYS = [
    { cat: "how.adaptive.cat1", tool: "how.adaptive.tool1" },
    { cat: "how.adaptive.cat2", tool: "how.adaptive.tool2" },
    { cat: "how.adaptive.cat3", tool: "how.adaptive.tool3" },
    { cat: "how.adaptive.cat4", tool: "how.adaptive.tool4" },
];

export default function HowItWorks() {
    const { t } = useI18n();
    return (
        <section
            id="come-funziona"
            data-testid="how-it-works"
            style={{
                position: "relative",
                background: "var(--itly-page)",
                padding: "96px 32px",
            }}
            data-reveal="up"
        >
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                <div style={{ textAlign: "center", maxWidth: 780, margin: "0 auto 56px" }}>
                    <p
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 12.5,
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: "var(--itly-orange-700)",
                            margin: 0,
                        }}
                    >
                        {t("how.badge")}
                    </p>
                    <h2
                        className="itly-serif"
                        style={{
                            marginTop: 14,
                            marginBottom: 0,
                            fontSize: "clamp(32px, 3.4vw, 46px)",
                            lineHeight: 1.1,
                            color: "var(--itly-fg-strong)",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {t("how.title_a")}{" "}
                        <em style={{ fontStyle: "italic", color: "var(--itly-orange-500)", fontWeight: 500 }}>
                            {t("how.title_b")}
                        </em>
                    </h2>
                </div>

                <div
                    className="itly-steps"
                    data-testid="steps"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                        gap: 20,
                    }}
                >
                    {STEP_KEYS.map((s) => (
                        <div
                            key={s.n}
                            className="itly-card"
                            data-testid={`step-${s.n}`}
                            data-reveal="up"
                            style={{
                                padding: "26px 24px 28px",
                                display: "flex",
                                flexDirection: "column",
                                gap: 12,
                                position: "relative",
                            }}
                        >
                            <span
                                style={{
                                    fontFamily: "Fraunces, serif",
                                    fontWeight: 700,
                                    fontSize: 40,
                                    lineHeight: 1,
                                    color: "var(--itly-fg-strong)",
                                    letterSpacing: "-0.03em",
                                }}
                            >
                                {s.n}
                            </span>
                            <div
                                style={{
                                    width: 24,
                                    height: 1,
                                    background: "var(--itly-orange-500)",
                                    marginTop: 4,
                                    marginBottom: 6,
                                }}
                            />
                            <h3
                                className="itly-serif"
                                style={{
                                    margin: 0,
                                    fontSize: 22,
                                    lineHeight: 1.15,
                                    color: "var(--itly-fg-strong)",
                                    letterSpacing: "-0.01em",
                                }}
                            >
                                {t(s.title)}
                            </h3>
                            <p
                                style={{
                                    margin: 0,
                                    fontFamily: "Inter, sans-serif",
                                    fontSize: 14.5,
                                    lineHeight: 1.6,
                                    color: "var(--itly-muted)",
                                }}
                            >
                                {t(s.body)}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Adaptive tools row */}
                <div
                    style={{
                        marginTop: 40,
                        padding: "24px 28px",
                        background: "var(--itly-surface)",
                        border: "1px solid var(--itly-border)",
                        borderRadius: 16,
                        boxShadow: "var(--itly-card-shadow)",
                        display: "grid",
                        gridTemplateColumns: "minmax(220px, 0.6fr) 1fr",
                        gap: 24,
                        alignItems: "center",
                    }}
                    className="itly-adaptive-wrap"
                >
                    <div>
                        <p
                            style={{
                                margin: 0,
                                fontFamily: "Inter, sans-serif",
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                                color: "var(--itly-orange-700)",
                            }}
                        >
                            {t("how.adaptive.badge")}
                        </p>
                        <h3
                            className="itly-serif"
                            style={{
                                margin: "6px 0 0",
                                fontSize: 22,
                                lineHeight: 1.15,
                                color: "var(--itly-fg-strong)",
                                letterSpacing: "-0.01em",
                            }}
                        >
                            {t("how.adaptive.title")}
                        </h3>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {ADAPTIVE_KEYS.map((a) => (
                            <div
                                key={a.cat}
                                style={{
                                    padding: "8px 14px",
                                    background: "var(--itly-teal-050)",
                                    borderRadius: 999,
                                    fontFamily: "Inter, sans-serif",
                                    fontSize: 12.5,
                                    color: "var(--itly-fg-strong)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                }}
                            >
                                <strong>{t(a.cat)}</strong>
                                <span style={{ color: "var(--itly-muted)" }}>·</span>
                                <span style={{ color: "var(--itly-muted)" }}>{t(a.tool)}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Extra tools: QR return (rentals), bookings view, real-time notifications */}
                <div
                    data-testid="how-extras"
                    style={{
                        marginTop: 40,
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: 16,
                    }}
                    className="itly-extras-grid"
                >
                    {[
                        { key: "bookings", glyph: "☰" },
                        { key: "notif", glyph: "◉" },
                    ].map((ex) => (
                        <div
                            key={ex.key}
                            data-testid={`how-extra-${ex.key}`}
                            style={{
                                background: "var(--itly-surface)",
                                border: "1px solid var(--itly-border)",
                                borderRadius: 16,
                                padding: "20px 22px",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 14,
                            }}
                        >
                            <span
                                aria-hidden="true"
                                style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 10,
                                    background: "var(--itly-peach-050)",
                                    color: "var(--itly-orange-500)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontFamily: "Fraunces, serif",
                                    fontSize: 17,
                                    fontWeight: 600,
                                    flexShrink: 0,
                                }}
                            >
                                {ex.glyph}
                            </span>
                            <div>
                                <div
                                    style={{
                                        fontFamily: "Inter, sans-serif",
                                        fontSize: 14.5,
                                        fontWeight: 600,
                                        color: "var(--itly-fg-strong)",
                                        marginBottom: 4,
                                    }}
                                >
                                    {t(`how.extras.${ex.key}.title`)}
                                </div>
                                <div
                                    style={{
                                        fontFamily: "Inter, sans-serif",
                                        fontSize: 13,
                                        color: "var(--itly-muted)",
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {t(`how.extras.${ex.key}.body`)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
